import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { CalendarCheck, CalendarBlank, CheckCircle, CircleNotch, Phone, Sun, CloudSun, MoonStars, Moon } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { readBooking } from "@/lib/booking";
import { toISO, fromISO } from "@/lib/dates";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PhotoAttach from "@/components/PhotoAttach";
import { fireLeadConversion } from "@/adsConfig";
import { PHONE_DISPLAY, PHONE_TEL } from "@/seoData";

const WINDOWS = [
  ["Morning (8am–12pm)", Sun],
  ["Afternoon (12–4pm)", CloudSun],
  ["Evening (4–7pm)", MoonStars],
  ["Overnight (7pm–8am)", Moon],
];
const inp = "w-full border border-[#E4E4E7] px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E] bg-white";

function Summary({ sel, pct, overnight }) {
  const surcharge = sel && overnight && sel.estimate != null ? Math.round(sel.estimate * pct / 100) : 0;
  if (!sel) return (
    <p className="text-sm text-[#52525B]" data-testid="booking-no-selection">No items picked yet — tell us what you have in the notes and we'll confirm your price.</p>
  );
  return (
    <div data-testid="booking-summary">
      <div className="grid grid-cols-2 gap-2 mb-4">
        {[["curbside", "Curbside"], ["full", "Full-Service"]].map(([k, l]) => (
          <span key={k} data-testid={`booking-mode-${k}`} className={`text-center text-sm font-bold py-2 rounded-sm border-2 ${sel.service_type === k ? "border-[#46A01E] bg-[#F4FAF0] text-[#15306E]" : "border-[#E4E4E7] text-[#A1A1AA]"}`}>{l}</span>
        ))}
      </div>
      {sel.booking_type === "load" ? (
        <p className="font-display font-extrabold text-[#15306E]" data-testid="booking-load">{sel.load_label}</p>
      ) : (
        <ul className="space-y-1.5 text-sm" data-testid="booking-items">
          {sel.items.map((i) => (
            <li key={`${i.name}-${i.qty}-${i.price}`} className="flex justify-between gap-3"><span>{i.qty}× {i.name}</span><span className="text-[#52525B]">${i.qty * i.price}</span></li>
          ))}
        </ul>
      )}
      {surcharge > 0 && (
        <div className="flex justify-between text-sm text-[#B45309] border-t border-[#E4E4E7] mt-4 pt-3" data-testid="booking-surcharge">
          <span>Overnight surcharge (+{pct}%)</span><span>+${surcharge}</span>
        </div>
      )}
      <div className={`flex items-end justify-between border-t border-[#E4E4E7] pt-4 ${surcharge > 0 ? "mt-3" : "mt-4"}`}>
        <span className="text-sm text-[#52525B]">Estimated price</span>
        <span data-testid="booking-estimate" className="font-display font-black text-3xl text-[#46A01E]">${sel.estimate + surcharge}</span>
      </div>
      <p className="text-xs text-[#71717A] mt-2">Final price is confirmed on-site before we start. No hidden fees.</p>
    </div>
  );
}

export default function BookPickup() {
  const sel = readBooking(useLocation().search);
  const [f, setF] = useState({ name: "", phone: "", address: "", email: "", pickup_date: "", pickup_window: "", notes: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const [photos, setPhotos] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [closed, setClosed] = useState({});
  const [calOpen, setCalOpen] = useState(false);
  const [overnightPct, setOvernightPct] = useState(25);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`).then(({ data }) => setOvernightPct(data.overnightSurchargePct ?? 25)).catch(() => {});
  }, []);

  const loadBlocked = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/blocked-dates`).then(({ data }) => {
    setBlocked(data.dates); setClosed(data.windows || {}); return data;
  });
  useEffect(() => { loadBlocked().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    const m = document.querySelector('meta[name="robots"]');
    const prev = m?.getAttribute("content");
    m?.setAttribute("content", "noindex, nofollow");
    return () => { if (m && prev) m.setAttribute("content", prev); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.pickup_date) { setErr("Please choose a pickup date."); return; }
    if (!f.pickup_window) { setErr("Please choose a pickup time window."); return; }
    if (photos.some((p) => p.uploading)) { setErr("Please wait for your photos to finish uploading."); return; }
    setErr(""); setState("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        ...f, email: f.email || null, notes: f.notes || null,
        photos: photos.filter((p) => p.path).map((p) => ({ path: p.path, url: p.url, filename: p.filename })),
        service_type: sel?.service_type || "curbside", booking_type: sel?.booking_type || "items",
        load_label: sel?.load_label || null, items: sel?.items || [], estimate: sel?.estimate ?? null,
      });
      fireLeadConversion();
      setState("done");
    } catch (e2) {
      setState("idle");
      if (e2.response?.status === 409) {
        setErr(e2.response.data.detail);
        loadBlocked().then((b) => setF((p) => (b.dates.includes(p.pickup_date) ? { ...p, pickup_date: "", pickup_window: "" } : { ...p, pickup_window: "" })));
      } else setErr("Something went wrong. Please try again or call us.");
    }
  };

  return (
    <div className="bg-[#F4F4F5] min-h-screen">
      <Helmet><title>Book Your Junk Pickup | CleanOut247</title><meta name="description" content="Book same-day or scheduled junk removal pickup in San Diego County online. Pick a time window, add photos and get an upfront price from CleanOut247." /><link rel="canonical" href="https://cleanout247.com/book" /><meta name="robots" content="index, follow, max-image-preview:large" /></Helmet>
      <Header />
      <main className="px-5 lg:px-8 pt-28 sm:pt-40 lg:pt-48 pb-20">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#46A01E] font-display font-bold uppercase tracking-widest text-xs">Book a pickup</p>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-[#15306E] mt-2">Book your junk pickup</h1>
          {state === "done" ? (
            <div data-testid="booking-success" className="mt-8 bg-white border-t-4 border-[#46A01E] rounded-sm p-8 max-w-2xl">
              <CheckCircle size={44} weight="fill" className="text-[#46A01E]" />
              <h2 className="font-display font-black text-2xl text-[#15306E] mt-3">You're booked in!</h2>
              <p className="text-[#3F3F46] mt-2">Thanks, {f.name.split(" ")[0]}. We'll call or text <strong>{f.phone}</strong> shortly to confirm your {f.pickup_window.split(" ")[0].toLowerCase()} pickup on <strong>{f.pickup_date}</strong>.</p>
              {f.email && <p className="text-sm text-[#52525B] mt-2" data-testid="booking-success-email">A confirmation email is on its way to <strong>{f.email}</strong>.</p>}
              <Link to="/" data-testid="booking-home-link" className="inline-block mt-6 text-[#15306E] font-bold underline">Back to CleanOut247</Link>
              <div className="mt-6 border-t border-[#E4E4E7] pt-5" data-testid="booking-review-prompt">
                <p className="text-sm text-[#3F3F46]">After your pickup, would you leave us a quick Google review? It helps local neighbors find an honest junk removal crew.</p>
                <a href="https://search.google.com/local/writereview?placeid=ChIJx8Eov0VYD6wRPjfrK_i3LIE" target="_blank" rel="noreferrer" data-testid="booking-review-link"
                  className="inline-block mt-3 bg-[#15306E] hover:bg-[#0F2350] text-white text-sm font-bold px-4 py-2.5 rounded-sm">Review us on Google</a>
              </div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-5 gap-6 mt-8">
              <aside className="lg:col-span-2 bg-white rounded-sm p-6 border border-[#E4E4E7] h-fit">
                <h2 className="font-display font-extrabold text-lg text-[#15306E] mb-4">Your selection</h2>
                <Summary sel={sel} pct={overnightPct} overnight={f.pickup_window.startsWith("Overnight")} />
              </aside>
              <form onSubmit={submit} data-testid="booking-form" className="lg:col-span-3 bg-white rounded-sm p-6 border-t-4 border-[#46A01E] space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required data-testid="booking-name" className={inp} placeholder="Full name *" value={f.name} onChange={set("name")} />
                  <input required type="tel" data-testid="booking-phone" className={inp} placeholder="Phone number *" value={f.phone} onChange={set("phone")} />
                </div>
                <input required data-testid="booking-address" className={inp} placeholder="Pickup address (street, city, ZIP) *" value={f.address} onChange={set("address")} />
                <div>
                  <input type="email" data-testid="booking-email" className={inp} placeholder="Email (optional)" value={f.email} onChange={set("email")} />
                  <p className="text-xs text-[#71717A] mt-1" data-testid="booking-email-hint">Enter your email to get a booking confirmation email.</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-[#71717A] uppercase">Preferred pickup date *</label>
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="booking-date" className={`${inp} mt-1 flex items-center gap-2 text-left ${f.pickup_date ? "text-[#18181B]" : "text-[#A1A1AA]"}`}>
                        <CalendarBlank size={18} className="text-[#46A01E]" />
                        {f.pickup_date ? fromISO(f.pickup_date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Choose a date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" data-testid="booking-date-calendar">
                      <Calendar mode="single" selected={f.pickup_date ? fromISO(f.pickup_date) : undefined}
                        onSelect={(d) => { if (d) { const iso = toISO(d); setF((p) => ({ ...p, pickup_date: iso, pickup_window: (closed[iso] || []).includes(p.pickup_window) ? "" : p.pickup_window })); setCalOpen(false); } }}
                        disabled={[{ before: new Date() }, ...blocked.map(fromISO)]}
                        modifiers={{ booked: blocked.map(fromISO) }} modifiersClassNames={{ booked: "line-through text-[#C0392B]" }} />
                      {blocked.length > 0 && <p className="text-[11px] text-[#71717A] px-3 pb-3">Crossed-out days are fully booked.</p>}
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <p className="text-xs font-bold text-[#71717A] uppercase mb-1">Preferred time *</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup">
                    {WINDOWS.map(([w, Icon], i) => {
                      const full = (closed[f.pickup_date] || []).includes(w);
                      return (
                        <button key={w} type="button" role="radio" aria-checked={f.pickup_window === w} aria-disabled={full} disabled={full} data-testid={`booking-window-${i}`} onClick={() => setF((p) => ({ ...p, pickup_window: w }))}
                          className={`flex flex-col items-center gap-1 py-3 px-1 rounded-sm border-2 text-xs font-bold transition-colors disabled:cursor-not-allowed ${full ? "border-[#F4F4F5] bg-[#FAFAFA] text-[#A1A1AA]" : f.pickup_window === w ? "border-[#46A01E] bg-[#F4FAF0] text-[#15306E]" : "border-[#E4E4E7] text-[#52525B] hover:border-[#A1A1AA]"}`}>
                          <Icon size={20} weight="duotone" className={full ? "text-[#D4D4D8]" : "text-[#46A01E]"} /> {w.split(" (")[0]}<span className="font-normal text-[11px]">{w.split(" (")[1].replace(")", "")}</span>
                          {full ? <span className="font-bold text-[10px] text-[#C0392B]">Full</span> : w.startsWith("Overnight") && <span className="font-normal text-[10px] text-[#B45309] leading-tight">Extra charge</span>}
                        </button>
                      );
                    })}
                  </div>
                  {f.pickup_window.startsWith("Overnight") && <p data-testid="booking-overnight-note" className="text-xs text-[#B45309] mt-2">Overnight pickups have a +{overnightPct}% surcharge{sel?.estimate != null ? ` (+$${Math.round(sel.estimate * overnightPct / 100)})` : ""}, already added to your estimate.</p>}
                </div>
                <textarea data-testid="booking-notes" rows={3} className={inp} placeholder="Gate code, stairs, anything else we should know (optional)" value={f.notes} onChange={set("notes")} />
                <PhotoAttach photos={photos} setPhotos={setPhotos} testid="booking-photo" />
                {err && <p data-testid="booking-error" className="text-sm text-[#C0392B]">{err}</p>}
                <button type="submit" disabled={state === "sending"} data-testid="booking-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide py-4 rounded-sm transition-colors disabled:opacity-70">
                  {state === "sending" ? <CircleNotch size={18} className="animate-spin" /> : <CalendarCheck size={18} weight="fill" />} Request this pickup
                </button>
                <a href={`tel:${PHONE_TEL}`} className="flex items-center justify-center gap-2 text-sm text-[#15306E] font-semibold"><Phone size={15} weight="fill" /> Prefer to talk? Call/Text {PHONE_DISPLAY}</a>
              </form>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
